import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Search, Upload, Trash2, Edit, Copy, Image, Tag, X, Save, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import ImageUpload from "../../ImageUpload";
import { API, getImageUrl } from "./adminConfig";

const LazyImage = ({ src, alt, className, fallbackSrc }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before visible
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && !imageSrc && !hasError) {
      setImageSrc(src);
    }
  }, [isVisible, src, imageSrc, hasError]);

  const handleError = () => {
    if (!hasError && fallbackSrc) {
      setHasError(true);
      setImageSrc(fallbackSrc);
    }
  };

  return (
    <div ref={imgRef} className={className}>
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={alt}
          className="w-full h-full object-cover"
          onError={handleError}
        />
      ) : (
        <div className="w-full h-full bg-burgundy/10 flex items-center justify-center">
          <div className="animate-pulse w-8 h-8 rounded-full bg-burgundy/20"></div>
        </div>
      )}
    </div>
  );
};

// Image Library Management Tab
const ImageLibraryTab = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [tags, setTags] = useState([]);
  const [editingImageId, setEditingImageId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Support for multiple file uploads
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadFormData, setUploadFormData] = useState({
    file: null,
    name: '',
    description: '',
    tags: 'wellness,content'
  });
  // Cloudflare Stream video upload
  const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoUploading, setVideoUploading] = useState(false);
  const [pendingVideos, setPendingVideos] = useState([]);
  
  // Pagination state - load 24 images per page to reduce rate limiting
  const [currentPage, setCurrentPage] = useState(1);
  const IMAGES_PER_PAGE = 24;

  useEffect(() => {
    fetchImages();
    fetchTags();
  }, []);
  
  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedTag]);

  const fetchImages = async (search = '', tag = '') => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (tag) params.tag = tag;
      
      const response = await axios.get(`${API}/admin/image-library`, { params });
      setImages(response.data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Error loading image library');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/admin/image-library/tags`);
      setTags(response.data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    
    // For single file, use the old flow
    if (files.length === 1) {
      const file = files[0];
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      setUploadFormData({
        file,
        name: nameWithoutExt,
        description: '',
        tags: 'wellness,content'
      });
      setUploadFiles([]);
      setShowUploadModal(true);
    } else {
      // For multiple files, prepare batch upload
      const preparedFiles = files.map(file => ({
        file,
        name: file.name.replace(/\.[^/.]+$/, ""),
        description: '',
        tags: 'wellness,content',
        status: 'pending', // pending, uploading, complete, error
        progress: 0
      }));
      setUploadFiles(preparedFiles);
      setUploadFormData({ file: null, name: '', description: '', tags: 'wellness,content' });
      setUploadProgress({});
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    // Handle batch upload if multiple files
    if (uploadFiles.length > 0) {
      await handleBatchUpload();
      return;
    }
    
    // Single file upload
    if (!uploadFormData.file || !uploadFormData.name) {
      toast.error('Please provide a file and name');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFormData.file);
      formData.append('name', uploadFormData.name);
      formData.append('description', uploadFormData.description);
      formData.append('tags', uploadFormData.tags);

      const response = await axios.post(`${API}/admin/image-library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Image uploaded successfully!');
        setShowUploadModal(false);
        setUploadFormData({
          file: null,
          name: '',
          description: '',
          tags: 'wellness,content'
        });
        fetchImages();
        fetchTags();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleBatchUpload = async () => {
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    
    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < uploadFiles.length; i++) {
      const fileData = uploadFiles[i];
      
      // Update status to uploading
      setUploadFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));
      
      try {
        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('name', fileData.name);
        formData.append('description', fileData.description);
        formData.append('tags', fileData.tags);

        const response = await axios.post(`${API}/admin/image-library/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadFiles(prev => prev.map((f, idx) => 
              idx === i ? { ...f, progress } : f
            ));
          }
        });

        if (response.data.success) {
          successCount++;
          setUploadFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'complete', progress: 100 } : f
          ));
        } else {
          throw new Error('Upload failed');
        }
      } catch (error) {
        console.error(`Error uploading ${fileData.name}:`, error);
        errorCount++;
        setUploadFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', progress: 0 } : f
        ));
      }
    }
    
    setUploading(false);
    
    // Show summary
    if (successCount > 0 && errorCount === 0) {
      toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`);
      setShowUploadModal(false);
      setUploadFiles([]);
      fetchImages();
      fetchTags();
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${errorCount} failed`);
      fetchImages();
      fetchTags();
    } else {
      toast.error('All uploads failed');
    }
  };

  const updateFileMetadata = (index, field, value) => {
    setUploadFiles(prev => prev.map((f, idx) => 
      idx === index ? { ...f, [field]: value } : f
    ));
  };

  const removeFileFromQueue = (index) => {
    setUploadFiles(prev => prev.filter((_, idx) => idx !== index));
    if (uploadFiles.length === 1) {
      setShowUploadModal(false);
    }
  };

  // Cloudflare Stream Video Upload
  const handleVideoSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    if (videoFiles.length === 0) {
      toast.error('Please select video files');
      return;
    }
    
    const preparedVideos = videoFiles.map(file => ({
      file,
      name: file.name.replace(/\.[^/.]+$/, ""),
      description: '',
      tags: 'video,content',
      status: 'pending',
      progress: 0,
      videoUid: null
    }));
    
    setPendingVideos(preparedVideos);
    setShowVideoUploadModal(true);
  };

  const uploadVideoToStream = async (videoData, index) => {
    try {
      // Update status to uploading
      setPendingVideos(prev => prev.map((v, idx) => 
        idx === index ? { ...v, status: 'getting-url' } : v
      ));

      // 1. Get direct upload URL from our backend
      const urlResponse = await axios.post(`${API}/admin/stream/upload-url`, null, {
        params: { title: videoData.name }
      });

      if (!urlResponse.data.success) {
        throw new Error('Failed to get upload URL');
      }

      const { upload_url, video_uid } = urlResponse.data;

      // Update with video UID
      setPendingVideos(prev => prev.map((v, idx) => 
        idx === index ? { ...v, status: 'uploading', videoUid: video_uid } : v
      ));

      // 2. Upload video directly to Cloudflare
      const formData = new FormData();
      formData.append('file', videoData.file);

      await axios.post(upload_url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPendingVideos(prev => prev.map((v, idx) => 
            idx === index ? { ...v, progress } : v
          ));
        }
      });

      // 3. Save video reference to our library
      setPendingVideos(prev => prev.map((v, idx) => 
        idx === index ? { ...v, status: 'saving' } : v
      ));

      await axios.post(`${API}/admin/stream/video/${video_uid}/save`, null, {
        params: {
          name: videoData.name,
          description: videoData.description,
          tags: videoData.tags
        }
      });

      // Mark as complete
      setPendingVideos(prev => prev.map((v, idx) => 
        idx === index ? { ...v, status: 'complete', progress: 100 } : v
      ));

      return true;
    } catch (error) {
      console.error('Video upload error:', error);
      setPendingVideos(prev => prev.map((v, idx) => 
        idx === index ? { ...v, status: 'error', error: error.message } : v
      ));
      return false;
    }
  };

  const handleVideoUpload = async () => {
    setVideoUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pendingVideos.length; i++) {
      if (pendingVideos[i].status === 'pending') {
        const success = await uploadVideoToStream(pendingVideos[i], i);
        if (success) successCount++;
        else errorCount++;
      }
    }

    setVideoUploading(false);

    if (successCount > 0 && errorCount === 0) {
      toast.success(`Successfully uploaded ${successCount} video${successCount > 1 ? 's' : ''}!`);
      setShowVideoUploadModal(false);
      setPendingVideos([]);
      fetchImages();
      fetchTags();
    } else if (successCount > 0) {
      toast.warning(`Uploaded ${successCount} video${successCount > 1 ? 's' : ''}, ${errorCount} failed`);
      fetchImages();
      fetchTags();
    } else {
      toast.error('All video uploads failed');
    }
  };

  const updateVideoMetadata = (index, field, value) => {
    setPendingVideos(prev => prev.map((v, idx) => 
      idx === index ? { ...v, [field]: value } : v
    ));
  };

  const removeVideoFromQueue = (index) => {
    setPendingVideos(prev => prev.filter((_, idx) => idx !== index));
    if (pendingVideos.length === 1) {
      setShowVideoUploadModal(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/image-library/${imageId}`);
      toast.success('Image deleted successfully');
      fetchImages();
      fetchTags();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Error deleting image');
    }
  };

  const handleSearch = () => {
    fetchImages(searchQuery, selectedTag);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Auto-search after user stops typing for 300ms
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchImages(value, selectedTag);
    }, 300);
  };

  const handleTagChange = (e) => {
    const value = e.target.value;
    setSelectedTag(value);
    fetchImages(searchQuery, value);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading image library...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ position: 'relative', overflow: 'visible' }}>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-burgundy font-serif">Image Library</h2>
          <p className="text-burgundy/70">Manage your uploaded images ({images.length} images)</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Image/File Upload */}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id="image-upload"
            multiple
          />
          <label
            htmlFor="image-upload"
            className={`inline-flex items-center px-4 py-2 bg-burgundy hover:bg-burgundy/80 text-cream rounded-md cursor-pointer font-medium ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Images
          </label>
          
          {/* Video Upload (Cloudflare Stream) */}
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoSelect}
            disabled={videoUploading}
            className="hidden"
            id="video-upload"
            multiple
          />
          <label
            htmlFor="video-upload"
            className={`inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md cursor-pointer font-medium ${
              videoUploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Upload className="h-4 w-4 mr-2" />
            🎬 Upload Videos
          </label>
          
          <span className="text-sm text-burgundy/60 self-center">
            (Select multiple with Ctrl/Cmd+Click)
          </span>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Input
            placeholder="Search images by name, description, or tags..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            className="border-burgundy/30 focus:border-gold"
          />
        </div>
        <select
          value={selectedTag}
          onChange={handleTagChange}
          className="px-3 py-2 border border-burgundy/30 rounded-md focus:border-gold"
        >
          <option value="">All Tags</option>
          {tags.map(tag => (
            <option key={tag.tag} value={tag.tag}>
              {tag.tag} ({tag.count})
            </option>
          ))}
        </select>
        <Button onClick={handleSearch} className="bg-burgundy hover:bg-burgundy/80 text-cream">
          <Search className="h-4 w-4 mr-2" />
          Search
        </Button>
      </div>

      {/* Images Grid with Pagination */}
      {images.length === 0 ? (
        <div className="text-center py-12">
          <Image className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Images</h3>
          <p className="text-burgundy/50 mb-4">Upload your first image to get started.</p>
          <label
            htmlFor="image-upload"
            className="inline-flex items-center px-4 py-2 bg-burgundy hover:bg-burgundy/80 text-cream rounded-md cursor-pointer font-medium"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload First Image
          </label>
        </div>
      ) : (
        <>
        {/* Pagination Info */}
        <div className="flex justify-between items-center text-sm text-burgundy/70">
          <span>
            Showing {Math.min((currentPage - 1) * IMAGES_PER_PAGE + 1, images.length)}-{Math.min(currentPage * IMAGES_PER_PAGE, images.length)} of {images.length} images
          </span>
          <span className="text-xs text-burgundy/50">
            (Paginated to prevent rate limiting)
          </span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {images.slice((currentPage - 1) * IMAGES_PER_PAGE, currentPage * IMAGES_PER_PAGE).map((image) => (
            <Card key={image.id} className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20">
              <div className="relative">
                <LazyImage
                  src={getImageUrl(image.url)}
                  alt={image.description || image.name}
                  className="w-full h-32 rounded-t-lg overflow-hidden"
                  fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMjgiIGZpbGw9IiNmM2Y0ZjYiLz48cGF0aCBkPSJNNzUgNTVoNTB2MThoLTUwVjU1eiIgZmlsbD0iI2Q0ZWRkYSIvPjxwYXRoIGQ9Im05MCA1NSA4IDE4IDggMTgtMTYtMTh6IiBmaWxsPSIjYTdhYWFkIi8+PC9zdmc+"
                />
                <div className="absolute top-1 right-1 flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setEditingImageId(image.id);
                      setEditFormData({
                        name: image.name || '',
                        description: image.description || '',
                        tags: image.tags ? image.tags.join(', ') : ''
                      });
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-1 h-6 w-6 relative z-10 rounded"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      deleteImage(image.id);
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white p-1 h-6 w-6 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <CardContent className="p-3">
                {editingImageId === image.id ? (
                  // INLINE EDIT FORM
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const updateData = {
                        name: editFormData.name,
                        description: editFormData.description || '',
                        tags: (editFormData.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag)
                      };
                      console.log('Updating image with data:', updateData);
                      const response = await axios.put(`${API}/admin/image-library/${image.id}`, updateData);
                      console.log('Update response:', response.data);
                      toast.success('Image updated!');
                      setEditingImageId(null);
                      await fetchImages();
                      await fetchTags();
                    } catch (error) {
                      console.error('Error updating image:', error);
                      console.error('Error details:', error.response?.data);
                      toast.error(`Failed to update: ${error.response?.data?.detail || error.message}`);
                    }
                  }} className="space-y-2">
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      placeholder="Image name"
                      className="w-full px-2 py-1 text-sm border rounded focus:border-burgundy focus:outline-none"
                      required
                    />
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                      placeholder="Description"
                      rows={2}
                      className="w-full px-2 py-1 text-sm border rounded focus:border-burgundy focus:outline-none resize-none"
                    />
                    <input
                      type="text"
                      value={editFormData.tags}
                      onChange={(e) => setEditFormData({...editFormData, tags: e.target.value})}
                      placeholder="Tags (comma-separated)"
                      className="w-full px-2 py-1 text-sm border rounded focus:border-burgundy focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="flex-1 bg-burgundy text-white px-3 py-1 rounded text-sm hover:bg-burgundy/80">
                        Save
                      </button>
                      <button type="button" onClick={() => setEditingImageId(null)} className="flex-1 bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-300">
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  // NORMAL DISPLAY
                  <>
                    <h4 className="font-medium text-burgundy text-sm truncate">{image.name}</h4>
                    <p className="text-xs text-burgundy/70 truncate">{image.description || 'No description'}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {image.tags?.slice(0, 2).map(tag => (
                        <Badge key={tag} className="text-xs bg-burgundy/10 text-burgundy px-1 py-0">
                          {tag}
                        </Badge>
                      ))}
                      {image.tags?.length > 2 && (
                        <Badge className="text-xs bg-burgundy/10 text-burgundy px-1 py-0">
                          +{image.tags.length - 2}
                        </Badge>
                      )}
                      {(!image.tags || image.tags.length === 0) && (
                        <Badge className="text-xs bg-gray-100 text-gray-500 px-1 py-0">
                          No tags
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-burgundy/50 mt-1">
                      {image.width}×{image.height} • {Math.round(image.size / 1024)}KB
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {images.length > IMAGES_PER_PAGE && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <Button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              First
            </Button>
            <Button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {/* Page numbers */}
              {Array.from({ length: Math.ceil(images.length / IMAGES_PER_PAGE) }, (_, i) => i + 1)
                .filter(page => {
                  const totalPages = Math.ceil(images.length / IMAGES_PER_PAGE);
                  // Show first, last, current, and adjacent pages
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, idx, arr) => (
                  <React.Fragment key={page}>
                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                      <span className="text-burgundy/50 px-1">...</span>
                    )}
                    <Button
                      onClick={() => setCurrentPage(page)}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      className={currentPage === page 
                        ? "bg-burgundy text-cream" 
                        : "border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                      }
                    >
                      {page}
                    </Button>
                  </React.Fragment>
                ))}
            </div>
            
            <Button
              onClick={() => setCurrentPage(p => Math.min(Math.ceil(images.length / IMAGES_PER_PAGE), p + 1))}
              disabled={currentPage >= Math.ceil(images.length / IMAGES_PER_PAGE)}
              variant="outline"
              size="sm"
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Next
            </Button>
            <Button
              onClick={() => setCurrentPage(Math.ceil(images.length / IMAGES_PER_PAGE))}
              disabled={currentPage >= Math.ceil(images.length / IMAGES_PER_PAGE)}
              variant="outline"
              size="sm"
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Last
            </Button>
          </div>
        )}
        </>
      )}

      {/* Upload Modal - Single or Batch */}
      <Dialog open={showUploadModal} onOpenChange={(open) => {
        if (!open && !uploading) {
          setShowUploadModal(false);
          setUploadFiles([]);
          setUploadFormData({ file: null, name: '', description: '', tags: 'wellness,content' });
        }
      }}>
        <DialogContent className={uploadFiles.length > 0 ? "max-w-2xl max-h-[80vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="text-burgundy font-serif">
              {uploadFiles.length > 0 
                ? `Upload ${uploadFiles.length} Files to Library` 
                : 'Upload File to Library'}
            </DialogTitle>
            <DialogDescription>
              {uploadFiles.length > 0 
                ? 'Review and customize each file before uploading' 
                : 'Add description and tags for better SEO and organization'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Single file upload form */}
          {uploadFiles.length === 0 && uploadFormData.file && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  File: <span className="font-normal text-burgundy/70">{uploadFormData.file?.name}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Name *
                </label>
                <Input
                  value={uploadFormData.name}
                  onChange={(e) => setUploadFormData({...uploadFormData, name: e.target.value})}
                  placeholder="File name"
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Description (Alt Text for SEO)
                </label>
                <textarea
                  value={uploadFormData.description}
                  onChange={(e) => setUploadFormData({...uploadFormData, description: e.target.value})}
                  placeholder="E.g., Woman practicing yoga for menopause wellness"
                  rows={3}
                  className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:border-gold focus:outline-none resize-none"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  This will be used as alt text for search engines and accessibility
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Tags (comma-separated)
                </label>
                <Input
                  value={uploadFormData.tags}
                  onChange={(e) => setUploadFormData({...uploadFormData, tags: e.target.value})}
                  placeholder="wellness, health, menopause"
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFormData.file || !uploadFormData.name}
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFormData({
                      file: null,
                      name: '',
                      description: '',
                      tags: 'wellness,content'
                    });
                  }}
                  variant="outline"
                  className="flex-1 border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Batch upload form */}
          {uploadFiles.length > 0 && (
            <div className="space-y-4">
              {/* File list with individual progress */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {uploadFiles.map((fileData, index) => (
                  <div 
                    key={index} 
                    className={`border rounded-lg p-3 ${
                      fileData.status === 'complete' ? 'border-green-300 bg-green-50' :
                      fileData.status === 'error' ? 'border-red-300 bg-red-50' :
                      fileData.status === 'uploading' ? 'border-blue-300 bg-blue-50' :
                      'border-burgundy/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {fileData.file.type.startsWith('video/') ? (
                          <span className="text-purple-600">🎬</span>
                        ) : (
                          <span className="text-blue-600">🖼️</span>
                        )}
                        <span className="font-medium text-sm truncate max-w-48">
                          {fileData.file.name}
                        </span>
                        <span className="text-xs text-burgundy/50">
                          ({(fileData.file.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {fileData.status === 'complete' && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                        {fileData.status === 'error' && (
                          <AlertCircle className="h-5 w-5 text-red-600" />
                        )}
                        {fileData.status === 'pending' && (
                          <button 
                            onClick={() => removeFileFromQueue(index)}
                            className="text-red-500 hover:text-red-700"
                            disabled={uploading}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar for uploading files */}
                    {fileData.status === 'uploading' && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${fileData.progress}%` }}
                        />
                      </div>
                    )}
                    
                    {/* Editable fields for pending files */}
                    {fileData.status === 'pending' && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Input
                          value={fileData.name}
                          onChange={(e) => updateFileMetadata(index, 'name', e.target.value)}
                          placeholder="Name"
                          className="text-sm border-burgundy/30"
                          disabled={uploading}
                        />
                        <Input
                          value={fileData.tags}
                          onChange={(e) => updateFileMetadata(index, 'tags', e.target.value)}
                          placeholder="Tags (comma-separated)"
                          className="text-sm border-burgundy/30"
                          disabled={uploading}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Apply to all section */}
              {uploadFiles.some(f => f.status === 'pending') && !uploading && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Apply tags to all pending files:
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="wellness, health"
                      className="flex-1 border-burgundy/30"
                      id="batch-tags"
                    />
                    <Button
                      onClick={() => {
                        const tagsInput = document.getElementById('batch-tags');
                        const newTags = tagsInput?.value || '';
                        setUploadFiles(prev => prev.map(f => 
                          f.status === 'pending' ? { ...f, tags: newTags } : f
                        ));
                        toast.success('Tags applied to all pending files');
                      }}
                      variant="outline"
                      className="border-burgundy/30 text-burgundy"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleUpload}
                  disabled={uploading || uploadFiles.every(f => f.status !== 'pending')}
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading 
                    ? `Uploading... (${uploadFiles.filter(f => f.status === 'complete').length}/${uploadFiles.length})`
                    : `Upload ${uploadFiles.filter(f => f.status === 'pending').length} Files`}
                </Button>
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFiles([]);
                  }}
                  variant="outline"
                  className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  disabled={uploading}
                >
                  {uploading ? 'Please wait...' : 'Cancel'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Upload Modal (Cloudflare Stream) */}
      <Dialog open={showVideoUploadModal} onOpenChange={(open) => {
        if (!open && !videoUploading) {
          setShowVideoUploadModal(false);
          setPendingVideos([]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-burgundy font-serif flex items-center gap-2">
              🎬 Upload Videos to Cloudflare Stream
            </DialogTitle>
            <DialogDescription>
              Videos are uploaded to Cloudflare Stream for adaptive streaming and global delivery
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Video list with progress */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {pendingVideos.map((videoData, index) => (
                <div 
                  key={index} 
                  className={`border rounded-lg p-3 ${
                    videoData.status === 'complete' ? 'border-green-300 bg-green-50' :
                    videoData.status === 'error' ? 'border-red-300 bg-red-50' :
                    ['uploading', 'getting-url', 'saving'].includes(videoData.status) ? 'border-purple-300 bg-purple-50' :
                    'border-burgundy/20'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-purple-600 text-xl">🎬</span>
                      <span className="font-medium text-sm truncate max-w-64">
                        {videoData.file.name}
                      </span>
                      <span className="text-xs text-burgundy/50">
                        ({(videoData.file.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {videoData.status === 'complete' && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      {videoData.status === 'error' && (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                      {videoData.status === 'pending' && (
                        <button 
                          onClick={() => removeVideoFromQueue(index)}
                          className="text-red-500 hover:text-red-700"
                          disabled={videoUploading}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status text */}
                  {videoData.status === 'getting-url' && (
                    <p className="text-xs text-purple-600 mb-2">Preparing upload...</p>
                  )}
                  {videoData.status === 'uploading' && (
                    <p className="text-xs text-purple-600 mb-2">Uploading to Cloudflare Stream...</p>
                  )}
                  {videoData.status === 'saving' && (
                    <p className="text-xs text-purple-600 mb-2">Saving to library...</p>
                  )}
                  {videoData.status === 'error' && (
                    <p className="text-xs text-red-600 mb-2">Error: {videoData.error || 'Upload failed'}</p>
                  )}

                  {/* Progress bar */}
                  {['uploading', 'getting-url', 'saving'].includes(videoData.status) && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${videoData.progress}%` }}
                      />
                    </div>
                  )}

                  {/* Editable fields for pending videos */}
                  {videoData.status === 'pending' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Input
                        value={videoData.name}
                        onChange={(e) => updateVideoMetadata(index, 'name', e.target.value)}
                        placeholder="Video name"
                        className="text-sm border-burgundy/30"
                        disabled={videoUploading}
                      />
                      <Input
                        value={videoData.tags}
                        onChange={(e) => updateVideoMetadata(index, 'tags', e.target.value)}
                        placeholder="Tags (comma-separated)"
                        className="text-sm border-burgundy/30"
                        disabled={videoUploading}
                      />
                    </div>
                  )}

                  {/* Completed video info */}
                  {videoData.status === 'complete' && videoData.videoUid && (
                    <p className="text-xs text-green-600">
                      ✅ Video ID: {videoData.videoUid.substring(0, 12)}...
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Info box */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-800 mb-1">ℹ️ Cloudflare Stream Benefits:</p>
              <ul className="text-purple-700 text-xs space-y-1">
                <li>• Automatic adaptive bitrate streaming</li>
                <li>• Global CDN delivery for fast playback</li>
                <li>• Automatic thumbnail generation</li>
                <li>• Cost: ~$5/1000 mins storage + $1/1000 mins delivered</li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleVideoUpload}
                disabled={videoUploading || pendingVideos.every(v => v.status !== 'pending')}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Upload className="h-4 w-4 mr-2" />
                {videoUploading 
                  ? `Uploading... (${pendingVideos.filter(v => v.status === 'complete').length}/${pendingVideos.length})`
                  : `Upload ${pendingVideos.filter(v => v.status === 'pending').length} Video${pendingVideos.filter(v => v.status === 'pending').length !== 1 ? 's' : ''}`}
              </Button>
              <Button
                onClick={() => {
                  setShowVideoUploadModal(false);
                  setPendingVideos([]);
                }}
                variant="outline"
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                disabled={videoUploading}
              >
                {videoUploading ? 'Please wait...' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Edit Image Modal Component
const EditImageModal = ({ image, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: image?.name || '',
    description: image?.description || '',
    tags: image?.tags ? image.tags.join(', ') : ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        ...formData,
        tags: (formData.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      await axios.put(`${API}/admin/image-library/${image.id}`, updateData);
      toast.success('Image updated successfully!');
      onSave();
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Error updating image');
    } finally {
      setLoading(false);
    }
  };

  console.log('EditImageModal rendering with image:', image);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4" 
      style={{ 
        zIndex: 9999, 
        display: 'flex',
        visibility: 'visible',
        opacity: 1,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'auto'
      }}
      onClick={(e) => {
        // Close modal if clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        style={{ 
          display: 'block',
          visibility: 'visible',
          opacity: 1,
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '100%',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          pointerEvents: 'auto'
        }}
      >
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#7c2d12', margin: 0 }}>Edit Image</h2>
          <button 
            onClick={onClose} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'block' }}>
          {/* Image Preview */}
          <div style={{ marginBottom: '16px' }}>
            <img
              src={getImageUrl(image.url)}
              alt={image.name}
              style={{ 
                width: '100%', 
                height: '128px', 
                objectFit: 'cover', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}
            />
          </div>

          {/* Name Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#7c2d12', marginBottom: '8px' }}>
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Description Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#7c2d12', marginBottom: '8px' }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              placeholder="Describe this image..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Tags Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#7c2d12', marginBottom: '8px' }}>
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({...formData, tags: e.target.value})}
              placeholder="wellness, content, health (comma-separated)"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              Separate tags with commas
            </p>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px' }}>
            <button 
              type="button" 
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#7c2d12',
                color: '#fef3c7',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { ImageLibraryTab, LazyImage };
export default ImageLibraryTab;
